import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'

import axios from 'axios'
import download from 'downloadjs'
import classNames from 'classnames'
import { Helmet } from 'react-helmet'
import { toast } from 'react-toastify'

import {
    HydraTable,
    Heading,
    TextFilter,
    SelectFilter,
    DateRangeFilter,
    StyledCheckbox,
    TimeAgo,
} from '@peracto/peracto-ui'

import { useConfig } from '@peracto/peracto-config'
import { GET_LIST, useClient } from '@peracto/client'
import startCase from 'lodash/startCase'

const CategoryList = () => {
    // Default visible columns - key = column accessor
    const [selectedColumns, onSelectColumn] = useState({
        template: false,
        status: true,
        modifiedAt: true,
    })

    const { client } = useClient()

    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const API_URL = config.get('api')
    const { categories } = config.get('features', {})
    const categoryExports = categories?.exportCategoryData ?? true

    const exportCategoryData = async () => {
        let filename = `category.csv`

        try {
            const { data } = await axios.get(`${API_URL}/export/${filename}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            })

            download(data, `category_export.csv`)
        } catch (e) {
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
        }
    }

    // The 'selectable' property is used to define if the
    // column is available in the 'manage columns' dropdown
    const columns = useMemo(() => [
        {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <StyledCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => (
                <StyledCheckbox
                    {...row.getToggleRowSelectedProps()}
                    data-testid={`row-${row.index}-checkbox`}
                />
            ),
            selectable: false,
        },
        {
            Header: 'Name',
            accessor: 'heading',
            sortType: 'basic',
            Filter: props => <TextFilter label="Name" {...props} />,
            Cell: row => (
                <Link to={`${row.row.original.id}`} data-testid={`row-${row.row.index}-name`}>
                    {row.cell.value}
                </Link>
            ),
            selectable: false,
        },
        {
            Header: 'Template',
            accessor: 'template',
            sortType: 'basic',
            Filter: props => <TextFilter label="Template" {...props} />,
            show: selectedColumns['template'],
            selectable: true,
        },
        {
            Header: 'Type',
            accessor: 'type',
            sortType: 'basic',
            Filter: props => (
                <SelectFilter
                    label="Type"
                    options={[
                        { value: 'standard', label: 'Standard' },
                        { value: 'dynamic', label: 'Dynamic' },
                    ]}
                    {...props}
                />
            ),
            Cell: ({ cell: { value } }) => startCase(value),
            show: selectedColumns['type'],
            selectable: true,
        },
        {
            Header: 'Status',
            accessor: 'status',
            sortType: 'basic',
            Filter: props => (
                <SelectFilter
                    label="Status"
                    options={[
                        { value: 'active', label: 'Active' },
                        { value: 'draft', label: 'Draft' },
                        { value: 'deleted', label: 'Deleted' },
                    ]}
                    {...props}
                />
            ),
            Cell: ({ cell: { value } }) => (
                <span
                    className={classNames('badge', 'badge-pill', '!capitalize', {
                        'badge-success': value === 'active',
                        'badge-info': value === 'draft',
                        'badge-danger': value === 'deleted',
                    })}
                >
                    {value}
                </span>
            ),
            show: selectedColumns['status'],
            selectable: true,
        },
        {
            Header: 'Last Modified',
            accessor: 'modifiedAt',
            sortType: 'basic',
            Filter: props => <DateRangeFilter label="Last Modified" {...props} />,
            Cell: ({ cell: { value } }) =>
                value ? <TimeAgo title={formatDate(new Date(value))} date={value} /> : '-',
            show: selectedColumns['modifiedAt'],
            selectable: true,
        },
    ])

    const onFetchData = options => client(GET_LIST, 'categories', options)

    return (
        <>
            <Helmet>
                <title>Categories | Peracto</title>
            </Helmet>
            <Heading name="Categories">
                <div className="!flex !items-center !ml-2">
                    {categoryExports === true && (
                        <button
                            className="btn btn-outline-primary !mr-2"
                            onClick={() => exportCategoryData()}
                            data-testid="export-category-data"
                        >
                            <FontAwesomeIcon icon={faDownload} className="!mr-2" />
                            Export Category Data
                        </button>
                    )}

                    <Link
                        className="btn btn-primary"
                        to="/categories/add"
                        data-testid="add-category"
                    >
                        <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" />
                        Add Category
                    </Link>
                </div>
            </Heading>

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                columns={columns}
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                                defaultFilters={{
                                    heading: '',
                                    status: 'active',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default CategoryList
